import React, { useEffect, useState } from "react";
import { Box, Text } from "@chakra-ui/react";
import { Link } from "components/Link";
import { ChevronUpIcon, ChevronDownIcon } from "components/SvgIcons";
import Markdown from "markdown-to-jsx";
import { useSegment } from "tracking";
import { colours } from "@shared/ui.design-system";

type Props = {
  text: string;
  suitableFor?: string;
  isPLP?: boolean;
  isGiftMembership?: boolean;
};

const scrollThreshold = 120;

export const ReadMoreV2: React.FC<Props> = ({
  text,
  suitableFor,
  isPLP = false,
  isGiftMembership = false
}) => {
  const [isReadMore, setIsReadMore] = useState(!isGiftMembership);
  const [hasOverflow, setHasOverflow] = useState(false);
  const textRef = React.createRef<HTMLDivElement>();
  const { track } = useSegment();
  const linkLocation = isPLP ? "PLP" : "PDP";

  const toggleReadMore = () => {
    setIsReadMore(!isReadMore);
    if (isReadMore) {
      track({
        event: "Page Element Clicked",
        properties: {
          module_content: `${linkLocation} Read More`
        }
      });
    }
  };

  useEffect(() => {
    if (textRef?.current) {
      const { offsetHeight } = textRef.current;

      if (offsetHeight > scrollThreshold && !isGiftMembership) {
        setHasOverflow(true);
      }
    }
  }, [textRef, isGiftMembership]);

  let boxHeight = "unset";
  let readMore: string | JSX.Element = "Show less";

  if (isReadMore && hasOverflow) {
    boxHeight = isPLP ? "5rem" : "3.5rem";
    readMore = isPLP ? (
      <ChevronDownIcon stroke={colours.primary.dark} strokeWidth="1" />
    ) : (
      "Read More"
    );
  } else if (isPLP) {
    readMore = <ChevronUpIcon stroke={colours.primary.dark} strokeWidth="1" />;
  }

  const fadeEffect =
    isPLP && isReadMore
      ? {
          "@media (max-width: 701px)": {
            content: "''",
            position: "absolute",
            left: "0",
            top: "0",
            width: "100%",
            height: "100%",
            background: "linear-gradient(transparent 2.5rem, white)"
          }
        }
      : { content: "''" };

  return (
    <>
      <Box
        ref={textRef}
        maxHeight={boxHeight}
        lineHeight={0}
        overflow="hidden"
        position="relative"
        _before={fadeEffect}
        onClick={() => {
          if (hasOverflow) toggleReadMore();
        }}
      >
        <Markdown
          as="span"
          options={{
            overrides: {
              span: {
                component: Text,
                props: {
                  variant: "acuminLight",
                  as: "span",
                  marginTop: "0.5rem",
                  display: "block"
                }
              },
              p: {
                component: Text,
                props: {
                  variant: "acuminLight",
                  marginTop: "0.5rem",
                  display: "block"
                }
              },
              a: {
                component: Link,
                props: {
                  variant: "acuminMedium",
                  fontFamily: "inherit",
                  fontSize: "inherit"
                }
              }
            }
          }}
        >
          {text}
        </Markdown>
        {suitableFor && (
          <Text variant="acuminLight" py={0.5}>
            {suitableFor}
          </Text>
        )}
      </Box>
      {hasOverflow && (
        <Text
          data-testid="ReadMore"
          textDecoration="underline"
          variant="acuminRegular"
          mt={isPLP ? 0 : 1}
          mb={5}
          onClick={toggleReadMore}
          sx={{
            ":hover": { cursor: "pointer" }
          }}
          textAlign={isPLP ? "center" : "left"}
        >
          {readMore}
        </Text>
      )}
    </>
  );
};
